import React from "react";
import { useNavigate } from "react-router-dom";
import {
  BellRing,
  Building2,
  LayoutDashboard,
  ListChecks,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  Wrench,
} from "lucide-react";
import {
  CtaSection,
  Section,
  SectionHeading,
  btnOutlineDark,
  btnPrimary,
  cardClass,
  cardIconClass,
  cardTitleClass,
  useAuthActions,
} from "../components/SiteUI";

const Home = () => {
  const navigate = useNavigate();
  const { openSignup } = useAuthActions();

  const focusAreas = [
    {
      title: "Operations Control",
      text: "Centralize property records, unit details, and ownership documents in one secure workspace.",
      icon: Building2,
    },
    {
      title: "Tenant Lifecycle",
      text: "Handle onboarding, lease renewals, communication, and history tracking without manual spreadsheets.",
      icon: Users,
    },
    {
      title: "Revenue Visibility",
      text: "Track rent, invoices, and arrears in real time with clear summaries and downloadable reports.",
      icon: TrendingUp,
    },
  ];

  const highlights = [
    { text: "Portfolio-level dashboard with occupancy and revenue indicators", icon: LayoutDashboard },
    { text: "Automated rent reminders and structured invoice records", icon: BellRing },
    { text: "Maintenance workflow from request creation to completion", icon: Wrench },
    { text: "Role-based access for admins, managers, and staff", icon: ShieldCheck },
  ];

  return (
    <div className="bg-teal-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-teal-800 to-teal-900" aria-hidden="true" />
        <div
          className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,#5eead4_0%,transparent_50%)]"
          aria-hidden="true"
        />

        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-24 sm:pt-24 sm:pb-32 text-center">
          <p className="hero-in hero-in-1 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-xs font-semibold text-white/80 mb-6">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10.5 12 3l9 7.5M5 9v11h14V9" />
            </svg>
            Rental Management Suite
          </p>

          <h1 className="hero-in hero-in-2 text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] max-w-3xl mx-auto">
            Property Management, <span className="text-teal-300">Simplified</span>
          </h1>

          <p className="hero-in hero-in-3 mt-6 text-lg sm:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
            Gamrent gives property owners and managers a dependable, straightforward platform to coordinate operations, finance, and tenant communication with complete clarity.
          </p>

          <div className="hero-in hero-in-4 mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={openSignup}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-teal-300 hover:bg-teal-400 text-teal-900 text-base font-semibold shadow-lg shadow-black/10 transition-colors"
            >
              Create Account
            </button>
            <button
              onClick={() => navigate("/features")}
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-full border-2 border-white/30 hover:bg-white/10 text-white text-base font-semibold transition-colors"
            >
              Explore Features
            </button>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeading eyebrow="Core Focus Areas" title={"Designed for day\u2011to\u2011day property operations"} />
        <div className="grid md:grid-cols-3 gap-6">
          {focusAreas.map((item, idx) => (
            <article key={item.title} className={`reveal reveal-delay-${idx + 1} lift ${cardClass}`}>
              <div className={`${cardIconClass} mb-4`} aria-hidden="true">
                <item.icon className="w-5 h-5" />
              </div>
              <h3 className={cardTitleClass}>{item.title}</h3>
              <p className="text-sm text-teal-800 mt-2 leading-relaxed">{item.text}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section tone="alt">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">
          <div className="space-y-6">
            <SectionHeading
              align="left"
              eyebrow="Why Teams Choose Gamrent"
              title="A practical platform with clear workflows"
              description="Built to mirror how rental operations really work, so staff can onboard quickly and managers can make decisions faster."
            />

            <div className={`reveal ${cardClass}`}>
              <h3 className={`${cardTitleClass} flex items-center gap-2`}>
                <ListChecks className="w-5 h-5 text-teal-700" aria-hidden="true" />
                Activation Flow
              </h3>
              <ol className="mt-5 space-y-3.5 text-sm text-teal-800">
                {[
                  "Sign up with your company email. Use that email to sign in.",
                  "Share payment evidence with Gamrent admin.",
                  "Get dashboard access after approval.",
                ].map((step, idx) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-700 text-white font-bold flex items-center justify-center text-xs">{idx + 1}</span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className={`reveal reveal-delay-1 ${cardClass}`}>
            <h3 className={`${cardTitleClass} flex items-center gap-2`}>
              <Sparkles className="w-5 h-5 text-teal-700" aria-hidden="true" />
              Platform Highlights
            </h3>
            <ul className="mt-5 space-y-4">
              {highlights.map(({ text, icon: Icon }) => (
                <li key={text} className="flex items-start gap-3">
                  <span className="w-5 h-5 mt-0.5 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3 h-3" aria-hidden="true" />
                  </span>
                  <span className="text-sm text-teal-800 leading-relaxed">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <CtaSection
        title="Start Managing Better in Minutes"
        description="Create your account with your company email. Contact Gamrent admin to enable your dashboard access, then sign in with that email."
      >
        <button onClick={openSignup} className={btnPrimary}>
          Create Account
        </button>
        <button onClick={() => navigate("/contact")} className={btnOutlineDark}>
          Contact Sales
        </button>
      </CtaSection>
    </div>
  );
};

export default Home;
